/** 単語一覧テーブル(ステージ詳細・横断検索で共用)。 */
import { useState } from 'react';
import { meaningText, type ContentWord } from '../services/content';
import { assetUrl } from '../services/env';

interface Props {
  readonly words: readonly ContentWord[];
  readonly assetsBase: string | null;
  /** 見つからなかった wordId(ステージ定義の参照切れ)。 */
  readonly missingIds?: readonly string[];
  /** 「どのステージで使われているか」を出す場合の逆引き。 */
  readonly stageIdsByWordId?: ReadonlyMap<string, readonly string[]>;
  readonly onRequest: (word: ContentWord) => void;
}

/** 代表画像。`images[0]`(多様提示の1枚目)を優先し、旧データは `image`。 */
function primaryImage(word: ContentWord): string | undefined {
  return word.media?.images?.[0] ?? word.media?.image;
}

/** 代表音声。英語音声の1本目 → 旧 `audio.en` → 日本語音声。 */
function primaryAudio(word: ContentWord): string | undefined {
  return (
    word.media?.audios?.en?.[0] ??
    word.media?.audio?.en ??
    word.media?.audios?.ja?.[0] ??
    word.media?.audio?.ja
  );
}

function play(url: string): void {
  const audio = new Audio(url);
  void audio.play().catch(() => undefined);
}

/**
 * サムネイル。
 *
 * アセットは content と別に生成されるので「content には path があるのに
 * ファイルがまだ無い」が普通に起きる。壊れた画像アイコンを出さず、
 * 「未生成」と分かる形にする(= 生成漏れを見つける手がかりになる)。
 */
function Thumb({ url }: { readonly url: string | null }) {
  const [failed, setFailed] = useState(false);
  if (url === null) return <div className="thumb thumb--empty">なし</div>;
  if (failed) return <div className="thumb thumb--empty">未生成</div>;
  return (
    <img
      className="thumb"
      src={url}
      alt=""
      loading="lazy"
      onError={() => {
        setFailed(true);
      }}
    />
  );
}

export function WordTable({
  words,
  assetsBase,
  missingIds = [],
  stageIdsByWordId,
  onRequest,
}: Props) {
  return (
    <>
      {missingIds.length > 0 && (
        <div className="notice notice--error" role="alert">
          参照切れの単語ID({String(missingIds.length)}件):{' '}
          <span className="mono">{missingIds.join(', ')}</span>
        </div>
      )}
      <table className="table">
        <thead>
          <tr>
            <th style={{ width: '68px' }}>画像</th>
            <th style={{ width: '92px' }}>ID</th>
            <th>表示 / 読み</th>
            <th>訳</th>
            <th>タグ</th>
            {stageIdsByWordId && <th>使用ステージ</th>}
            <th style={{ width: '150px' }}>操作</th>
          </tr>
        </thead>
        <tbody>
          {words.map((word) => {
            const image = assetUrl(primaryImage(word), assetsBase);
            const audio = assetUrl(primaryAudio(word), assetsBase);
            const stageIds = stageIdsByWordId?.get(word.id) ?? [];
            return (
              <tr key={word.id}>
                <td>
                  <Thumb url={image} />
                </td>
                <td className="mono">{word.id}</td>
                <td>
                  <div>{word.display}</div>
                  <div className="muted mono">{word.reading}</div>
                  {word.ruby !== undefined && word.ruby !== word.reading && (
                    <div className="muted mono">ruby: {word.ruby}</div>
                  )}
                </td>
                <td>{meaningText(word) ?? <span className="muted">—</span>}</td>
                <td>
                  {(word.tags ?? []).map((tag) => (
                    <span className="tag" key={tag}>
                      {tag}
                    </span>
                  ))}
                </td>
                {stageIdsByWordId && (
                  <td className="mono muted">
                    {stageIds.length === 0 ? '(未使用)' : stageIds.join(', ')}
                  </td>
                )}
                <td>
                  <button
                    className="button button--small"
                    type="button"
                    disabled={audio === null}
                    title={audio === null ? '音声アセットが未配備です' : '音声を再生'}
                    onClick={() => {
                      if (audio) play(audio);
                    }}
                  >
                    ▶ 音声
                  </button>{' '}
                  <button
                    className="button button--small"
                    type="button"
                    onClick={() => {
                      onRequest(word);
                    }}
                  >
                    修正メモ
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {words.length === 0 && <p className="muted">該当する単語はありません。</p>}
    </>
  );
}
